/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import React from "react";
import Paper from "@mui/material/Paper";
import FormFader from "../Common/Fader/FormFader";
import Grid from "@mui/material/Grid";
import FormSubmit from "../Common/FormSubmit/FormSubmit";
import { useForm } from "../../../provider/Form/FormProvider";
import ValidatorTextField from "../../FormFields/ValidatorTextField";
import { requiredValidator } from "../../../services/validationRules";
import FormLegend from "../Common/FormLegend/FormLegend";
import FormSubmitProps from "../../../types/FormSubmitProps";
import wizardStyle from "../wizardStyle";
import { Box } from "@mui/material";
import { fetchResource, queryKeys } from "../../../services/reactQuery/reactQueryService";
import { getProductData } from "../../../services/productService";
import WaitingForAiResponse from "../Common/WaitingForAiResponse/WaitingForAiResponse";
import { AbstractCase } from "../../../types/Entities/AbstractCase";
import { useQuery } from "@tanstack/react-query";

type ConcernSummaryProps = {
  submitProps: FormSubmitProps;
  product: AbstractCase;
};

export default function ConcernSummary({ submitProps, product }: ConcernSummaryProps) {
  const { handleSubmit } = useForm();

  useQuery({
    queryKey: queryKeys.item(getProductData(product.name, "apiUri"), product.id),
    queryFn: () => fetchResource(getProductData(product.name, "apiUri"), product.id),
    enabled: product.finalClientDescriptionAndObjectives === null,
    refetchInterval: (data) => {
      const stillWaiting = data.finalClientDescriptionAndObjectives === null;

      return stillWaiting ? 2000 : false;
    },
    refetchIntervalInBackground: true,
  });

  const isWaitingForAiResponse = product.finalClientDescriptionAndObjectives === null;

  if (isWaitingForAiResponse) {
    return (
      <WaitingForAiResponse
        mobileHeadline={"Zusammenfassung"}
        headline={"Wir fassen Ihr Anliegen abschließend zusammen"}
        text={"Ihr Anliegen wird gerade abschließend zusammengefasst.<br />Bitte laden Sie die Seite nicht neu."}
      />
    );
  }

  return (
    <Paper sx={wizardStyle.paper} elevation={4}>
      <FormFader>
        <Box component={"form"} sx={{ ...wizardStyle.applicationForm, ...wizardStyle.wizardForm }} onSubmit={handleSubmit}>
          <Grid container justifyContent={"flex-start"} alignItems={"baseline"}>
            <Grid item xs={12}>
              <FormLegend>
                <strong>Wir fassen Ihr Anliegen abschließend zusammen</strong>
              </FormLegend>
            </Grid>
            <Grid item xs={12} mb={4} sx={wizardStyle.gridTopSpaced}>
              <FormLegend>
                Bitte prüfen Sie, ob ihr Anliegen korrekt aufgenommen wurde. Sie können gerne Informationen ergänzen oder anpassen. Im nächsten Schritt haben
                Sie noch die Möglichkeit uns Dokumente zukommen zu lassen.
              </FormLegend>
            </Grid>
            <Grid item xs={12}>
              <ValidatorTextField
                name="finalClientDescriptionAndObjectives"
                label="Zusammenfassung des Anliegens"
                InputLabelProps={{
                  shrink: true,
                }}
                multiline
                rows={10}
                validators={[requiredValidator("Bitte ergänzen Sie die notwendigen Informationen.")]}
              />
            </Grid>
          </Grid>
          <FormSubmit {...submitProps} />
        </Box>
      </FormFader>
    </Paper>
  );
}
